import { type FormEvent, useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useTenant } from "../../hooks/useTenant";
import { formatDate, statusTone } from "../../lib/format";
import { workoutPlanService } from "../../services/workoutPlanService";
import type { WorkoutPlan } from "../../types/coaching";

const initialForm = {
  title: "",
  description: "",
  dayTitle: "",
  exerciseName: "",
  sets: "",
  reps: "",
  rest: "",
  notes: "",
};

export function TrainerWorkoutsPage() {
  const { currentTenant } = useTenant();
  const [plans, setPlans] = useState<WorkoutPlan[]>([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function loadPlans() {
    if (!currentTenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      setPlans(await workoutPlanService.listWorkoutPlansForTrainer(currentTenant.id));
    } catch (caughtError) {
      console.error("Unable to load workout plans", caughtError);
      setError("Unable to load workout plans.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPlans();
  }, [currentTenant?.id]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentTenant?.id || !form.title.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await workoutPlanService.createWorkoutPlan({
        tenantId: currentTenant.id,
        title: form.title,
        description: form.description,
        dayTitle: form.dayTitle,
        exerciseName: form.exerciseName,
        sets: form.sets,
        reps: form.reps,
        rest: form.rest,
        notes: form.notes,
        isTemplate: true,
      });
      setForm(initialForm);
      setSuccess("Workout plan created.");
      await loadPlans();
    } catch (caughtError) {
      console.error("Unable to create workout plan", caughtError);
      setError("Unable to create workout plan.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <AdminPageHeader
        eyebrow="Coaching content"
        title="Workout plans"
        description="Create simple workout plan templates and assign them from each client profile."
      />

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <AdminPanel title="Create workout plan" description="V1 supports a simple first session. More sessions can be added later.">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input label="Plan title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
            <Input label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            <Input label="Session title" value={form.dayTitle} onChange={(event) => setForm({ ...form, dayTitle: event.target.value })} placeholder="Session 1" />
            <Input label="First exercise" value={form.exerciseName} onChange={(event) => setForm({ ...form, exerciseName: event.target.value })} />
            <div className="grid gap-3 sm:grid-cols-3">
              <Input label="Sets" value={form.sets} onChange={(event) => setForm({ ...form, sets: event.target.value })} />
              <Input label="Reps" value={form.reps} onChange={(event) => setForm({ ...form, reps: event.target.value })} />
              <Input label="Rest" value={form.rest} onChange={(event) => setForm({ ...form, rest: event.target.value })} />
            </div>
            <label className="block text-sm font-medium text-ink">
              Notes
              <textarea className="mt-2 min-h-24 w-full rounded-md border border-line bg-field px-3.5 py-2.5 text-base text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            </label>
            {error ? <p className="text-sm font-medium text-red-800">{error}</p> : null}
            {success ? <p className="text-sm font-medium text-pine">{success}</p> : null}
            <Button isLoading={saving} type="submit">Create template</Button>
          </form>
        </AdminPanel>

        <AdminPanel title="Workout library">
          {loading ? <FullPageLoader label="Loading workout plans" /> : null}
          {!loading && !plans.length ? (
            <EmptyState title="No workout plans yet.">Create one first, then assign it from a client profile.</EmptyState>
          ) : null}
          <div className="space-y-3">
            {plans.map((plan) => (
              <div className="rounded-md border border-line bg-white p-4" key={plan.id}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="font-semibold text-ink">{plan.title}</h2>
                    <p className="mt-1 text-sm text-graphite/70">{plan.description || "No description."}</p>
                    <p className="mt-2 text-xs text-graphite/55">
                      {plan.client_id ? `Assigned ${formatDate(plan.assigned_at)}` : "Template/unassigned"}
                    </p>
                  </div>
                  <Badge tone={statusTone(plan.status)}>{plan.status}</Badge>
                </div>
                <div className="mt-3 space-y-2">
                  {(plan.workout_plan_days ?? []).map((day) => (
                    <div className="rounded-md bg-field p-3 text-sm" key={day.id}>
                      <p className="font-medium text-ink">{day.title}</p>
                      {(day.workout_plan_exercises ?? []).map((exercise) => (
                        <p className="mt-1 text-graphite/70" key={exercise.id}>
                          {exercise.exercise_name} {exercise.sets ? `${exercise.sets} sets` : ""} {exercise.reps ? `x ${exercise.reps}` : ""}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AdminPanel>
      </div>
    </div>
  );
}
